import { useAuth0, User } from '@auth0/auth0-react'
import * as client from '@cimpress-technology/logistics-configuration-client'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import * as uuid from 'uuid'
import * as models from '../../../common/models'
import { getAllLightLocations } from '../../../common/proxy/locations-store'
import * as qcmStore from '../../../common/proxy/qcm-store'
import { LinkData } from '../view-location-page/QcmLink'
import MigrationStatusTable, {
  FlagStatus,
} from './location-migration-status-table'

const hiddenFlags = new Set([
  'use-working-day-calendars-in-quoter',
  'use-transit-calendars-in-quoter',
  'cs-to-pickup-mapping-quoter',
  'use-country-calendars-in-quoter',
])

const getFeatureFlagsMapping = async (
  accessToken: string,
  locationId: string
) => {
  const flagIds = Object.keys(
    await client.locations.getAllFlagsByLocation(
      accessToken,
      uuid.v4(),
      locationId
    )
  )

  const promises = flagIds
    .filter(id => id !== '_links' && !hiddenFlags.has(id))
    .map(async flag => {
      const ff = await client.featureFlags.getLocationsByFlag(
        accessToken,
        uuid.v4(),
        flag
      )

      return {
        [flag]: ff.reduce((accum: string[], value) => {
          if (value.value) {
            accum.push(value.locationId)
          }

          return accum
        }, []),
      }
    })

  return Promise.all(promises)
}

const getQcmLinks = async (
  accessToken: string,
  user: User | undefined,
  locations: models.Location[]
): Promise<Record<string, LinkData[]>> => {
  return locations.reduce(
    async (acc: Promise<Record<string, LinkData[]>>, location) => {
      const resolvedAcc = await acc
      resolvedAcc[location.id] = await Promise.all(
        location.fulfillmentLocations.map(async fl => ({
          id: fl.id,
          link: await qcmStore.getQcmLink(
            accessToken,
            user,
            location.id,
            fl.id
          ),
          flName: fl.name,
          fulfillerName: fl.fulfiller.name,
        }))
      )

      return resolvedAcc
    },
    Promise.resolve({})
  )
}

export default function LocationMigrationStatusContainer() {
  const { getAccessTokenSilently, user } = useAuth0()
  const [loading, setLoading] = React.useState(false)
  const [locations, setLocations] = React.useState<models.Location[]>([])
  const [qcmLinks, setQcmLinks] = React.useState<Record<string, LinkData[]>>({})
  const [flags, setFlags] = React.useState<{ [key: string]: string[] }>({})

  const reloadLocations = React.useCallback(async () => {
    setLoading(true)
    const accessToken = await getAccessTokenSilently()
    const sub = user?.sub ?? ''
    const allLocations = await getAllLightLocations(accessToken, sub)
    const allFeatureFlags = await getFeatureFlagsMapping(
      accessToken,
      allLocations.keys().next().value
    )

    const values = Array.from(allLocations.values())
    setLocations(values)
    setFlags(Object.assign({}, ...allFeatureFlags))
    setLoading(false)

    return values
  }, [getAccessTokenSilently, user])

  const toggleFeatureFlag = async (
    locationId: string,
    locationName: string,
    featureFlagId: string,
    value: FlagStatus
  ): Promise<void> => {
    const confirmText = `You sure you want to turn ${
      value === 'on' ? 'off' : 'on'
    } ${featureFlagId} for ${locationName}?`
    const confirmed = window.confirm(confirmText)

    if (confirmed && value === 'off') {
      await client.featureFlags.putFeatureFlag(
        await getAccessTokenSilently(),
        uuid.v4(),
        featureFlagId,
        locationId,
        true
      )
      await reloadLocations()

      return
    }

    if (confirmed && value === 'on') {
      await client.featureFlags.deleteFeatureFlag(
        await getAccessTokenSilently(),
        uuid.v4(),
        featureFlagId,
        locationId
      )
      await reloadLocations()

      return
    }
  }

  React.useEffect(() => {
    const fetchData = async () => {
      const accessToken = await getAccessTokenSilently()
      const allLocations = await reloadLocations()
      const allQcmLinks = await getQcmLinks(accessToken, user, allLocations)
      setQcmLinks(allQcmLinks)
    }
    fetchData()
  }, [getAccessTokenSilently, reloadLocations, user])

  return (
    <Wrapper>
      <Helmet>
        <title>Migration status</title>
      </Helmet>
      <MigrationStatusTable
        flags={flags}
        qcmLinks={qcmLinks}
        locations={locations}
        toggleFeatureFlag={toggleFeatureFlag}
        loading={loading}
      />
    </Wrapper>
  )
}

function Wrapper(props: { children: React.ReactNode }) {
  return <main className="App-content main-container">{props.children}</main>
}
